/**
 * Insufficient credits modal (Phase 13.F).
 *
 * Triggered by HTTP 402 responses from any backend endpoint that
 * tried to charge credits. Friendly message + "Top up" CTA.
 *
 * Usage:
 *   const [showInsuf, setShowInsuf] = useState(null);
 *   ...
 *   if (resp.status === 402) {
 *     setShowInsuf({needed: resp.body.needed, operation: resp.body.operation});
 *   }
 *   <InsufficientCreditsModal open={!!showInsuf} {...showInsuf} onClose={() => setShowInsuf(null)} />
 */

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Stack, Alert,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';

const InsufficientCreditsModal = ({ open, needed, operation, message, onClose }) => {
  const navigate = useNavigate();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningAmberIcon color="warning" />
          <span>Insufficient credits</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning">
            {message ||
              `This action would cost ${needed?.toLocaleString?.() ?? '?'} credits, but your balance is too low.`}
          </Alert>
          {operation && (
            <Typography variant="caption" color="text.secondary">
              Operation: <code>{operation}</code>
            </Typography>
          )}
          <Typography variant="body2">
            You can:
          </Typography>
          <Typography component="ul" variant="body2" sx={{ pl: 3, m: 0 }}>
            <li>Wait for your next monthly grant</li>
            <li>Top up your balance now</li>
            <li>Reduce the scope of the operation (e.g. use the <em>adapt</em> intensity instead of <em>specialize</em>)</li>
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="outlined" onClick={() => { onClose?.(); navigate('/settings/billing'); }}>
          View credits
        </Button>
        <Button
          variant="contained" color="primary"
          onClick={() => window.location.assign(
            'mailto:support@celmind.ai?subject=Credit top-up'
          )}
        >
          Top up
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InsufficientCreditsModal;
